import { Mutex } from 'async-mutex';
import { IAttachmentFile, IRequest, IRequestAttachmentsField } from 'types';
import { GW_SERVICES_PREFIXES } from '../constants';

import { apiErrorHandler } from './api-error-handler';
import { tokenRefreshCoordinator } from './token-refresh-coordinator';

export const refresh_tokens_mutex = new Mutex();
const logout_mutex = new Mutex();
const { AUTHORIZATION } = GW_SERVICES_PREFIXES;

const relocateToAuthPageWithReload = () => {
  document.location.assign(`${document.location.origin}/auth/login/`);
  // Вместо очистки всех данных в store
  document.location.reload();
};

export class ResponseError extends Error {
  public response: Response;
  public errorMessage: string[] = [];
  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}

//parses JSON returned by request
function parseJSON(response: Response) {
  if (response.status === 200) {
    if (response.url.includes('control_info')) {
      //TODO: костыль, запрос отдаёт некорректный ответ и фронтенд падает. Возможно дело в хэдерах ответа и это поможет:
      // "Content-Type": "application/json",
      // "Access-Control-Allow-Origin": "*",
      // "Access-Control-Allow-Credentials" : true
      return true;
    }

    return response.json();
  } else if ([201, 202].includes(response.status)) {
    return response.json();
  } else if (response.status === 204) {
    /* Понадобилось при удалении категорий в документообороте */
    return response;
  }

  throw response;
}

//check response status code
// async function checkStatus(response: Response, url: string) {
//   if (response.status >= 200 && response.status < 300) {
//     return response;
//   } else if (response.status === 401 && url !== '/api/v4/auth/current/') {
//     window.location.reload();
//   }
//   // const error = new ResponseError(response);
//   // error.response = response;

//   let error: Promise<any>;

//   if (response.status === 406 || response.status === 403) {
//     error = await response.json();
//     // error.errorMessage = message.errors;
//     throw error;
//   }
//   // throw error

//   // if (response.status === 500) {
//   //   const message = await response.json();
//   //   error.errorMessage = message.errors;
//   //   window.open(`https://stackoverflow.com/seracr?q=${error.errorMessage}`, '_black')
//   // }
//   apiErrorHandler(response); //TODO: пока не несёт смысла, но в дальнейшем упростит кейсы обработки уникальных ошибок по статусу, не загрязняя эту функцию
//   // throw error;
//   return response;
// }

async function checkStatus(response: Response, url: string) {
  if (response.ok) {
    return response;
  }

  if ([401, 403, 406, 429].includes(response.status)) {
    throw await response.json();
  }

  apiErrorHandler(response);

  return response;
}

/**
 * Определяет, нужно ли повторять запрос после ошибки.
 *
 * Класс ошибок, которые мы считаем временными и повторяем:
 *   1. Сетевые ошибки fetch — когда запрос не дошёл до сервера вообще.
 *      Браузер отдаёт их как TypeError: 'Failed to fetch' / 'NetworkError' /
 *      'Load failed'. Сюда попадают ERR_NETWORK_CHANGED, ERR_INTERNET_DISCONNECTED,
 *      ERR_CONNECTION_RESET и т.п. — в DevTools они видны по-разному, но в JS
 *      приходят одним и тем же TypeError.
 *   2. Любой 5xx от сервера или прокси (500, 502, 503, 504).
 *   3. «200 + Service is unavailable» — бэк иногда отдаёт 200 OK с текстовым
 *      телом вместо JSON (за прокси/на техработах). parseJSON падает на
 *      response.json() с SyntaxError 'Unexpected token S...'.
 *
 * Всё остальное (4xx, AbortError, бизнес-ошибки) — не ретраим.
 */
function isRetryable(e: unknown): boolean {
  if (isAbortError(e)) {
    return false;
  }

  // Сетевые ошибки fetch
  if (e instanceof TypeError) {
    const msg = String((e as Error).message || '');

    if (/Failed to fetch|NetworkError|Load failed/i.test(msg)) {
      return true;
    }
  }

  // 5xx ошибки
  if (is5xx(e)) {
    return true;
  }

  // 200 + Service is unavailable
  if (e instanceof SyntaxError && /Unexpected token S/i.test(e.message)) {
    return true;
  }

  return false;
}

/**
 * Проверяет, что ошибка — это 5xx.
 *
 * Источники ошибок:
 *   - gatewayRequest → baseRequest → checkStatus → parseJSON делает `throw response`
 *     и наружу летит голый Response.
 *   - ResponseError оборачивает Response в случае, если его бросает другой слой.
 *   - На случай нестандартной обёртки проверяем ещё и поле `status` на самой ошибке.
 */
function is5xx(e: unknown): boolean {
  // parseJSON в request.ts делает `throw response` — летит голый Response
  if (e instanceof Response) {
    return e.status >= 500 && e.status <= 599;
  }

  // ResponseError из request.ts
  const resp = (e as any)?.response;

  if (resp && typeof resp.status === 'number') {
    return resp.status >= 500 && resp.status <= 599;
  }

  // на всякий случай
  const status = (e as any)?.status;

  return typeof status === 'number' && status >= 500 && status <= 599;
}

/**
 * Задержки между повторными попытками.
 * Итого: 1 основной запрос + 2 повтора = 3 попытки.
 * Паузы: 1s и 3s. После третьей неудачи — сдаёмся.
 */
const RETRY_DELAYS = [1000, 3000];

/**
 * Отменяемая пауза. Если signal прервётся во время сна — отклоняет промис
 * с AbortError, чтобы withNetworkRetry сразу вышел, не дожидаясь конца паузы.
 */
const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });

/**
 * Оборачивает асинхронный вызов в ретраи с паузами.
 *
 * Используется для GET-запросов результата отчёта в управленческом учёте:
 * после успешного ws-события идёт GET, и если он падает по сетевой причине,
 * без ретраев пользователь остаётся без отчёта (а раньше — вообще висел
 * с залоченной кнопкой «Построить»).
 *
 * Алгоритм:
 *   - 1 основной вызов + до 2 повторов (см. RETRY_DELAYS).
 *   - AbortError и прерванный signal — не ретраим, пробрасываем наверх.
 *   - Ошибки, не прошедшие isRetryable — не ретраим.
 *   - Если все попытки упали — бросаем последнюю ошибку.
 *
 * @param fn     — фабрика промиса (не сам промис, потому что нужно звать заново).
 * @param signal — AbortSignal для отмены (см. MR3, AbortController в сторе).
 */
export async function withNetworkRetry<T>(
  fn: () => Promise<T>,
  signal?: AbortSignal,
  onAttempt?: (attempt: number) => void
): Promise<T> {
  let lastErr: unknown;

  for (let i = 0; i <= RETRY_DELAYS.length; i++) {
    try {
      onAttempt?.(i + 1);

      return await fn();
    } catch (e) {
      if (signal?.aborted || isAbortError(e)) {
        throw e;
      }

      if (!isRetryable(e)) {
        throw e;
      }

      lastErr = e;

      if (i < RETRY_DELAYS.length) {
        await sleep(RETRY_DELAYS[i], signal);
      }
    }
  }

  throw lastErr;
}

//requests URL
export async function request(
  url: string,
  options?: RequestInit
): Promise<any | { err: ResponseError }> {
  const fetchResponse = await fetch(url, options);
  const response = await checkStatus(fetchResponse, url);

  return parseJSON(response);
}

export async function unparsedRequest(
  url: string,
  options?: RequestInit
): Promise<any | { err: ResponseError }> {
  const fetchResponse = await fetch(url, options);

  return await checkStatus(fetchResponse, url);
}

/**
 * Refresh the token if necessary and retry the request
 *
 * @param {string} url The original request URL
 * @param {RequestInit} options The options for the original request
 * @return {Promise<Response>} The response after the retry
 */
async function refreshTokenAndRetry(
  url: string,
  options?: RequestInit
): Promise<Response> {
  // Координация между вкладками через localStorage
  if (!tokenRefreshCoordinator.tryAcquireLock()) {
    // Другая вкладка уже делает refresh — ждём и повторяем запрос
    await tokenRefreshCoordinator.waitForRefresh();

    return fetch(url, { ...options, credentials: 'include' });
  }

  const release = await refresh_tokens_mutex.acquire();

  try {
    const response = await fetch(`/${AUTHORIZATION}/auth/refresh/`, {
      method: 'POST',
      credentials: 'include',
    });

    if (response.ok) {
      /** жду обработки Set-Cookie, чтобы новые токены гарантированно применились */
      await response.json().catch(() => {});
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Уведомляем другие вкладки об успешном refresh
      tokenRefreshCoordinator.notifyRefreshComplete();

      return fetch(url, { ...options, credentials: 'include' });
    }

    const token = document.cookie
      .split('; ')
      .find((row) => row.startsWith('refresh_token='))
      ?.split('=')[1];

    const errorStr = `Failed to refresh token from URL ${url} with TOKEN: ${token?.slice(
      0,
      10
    )}...`;

    console.error(errorStr);
    tokenRefreshCoordinator.releaseLock();

    throw new Error(errorStr);
  } finally {
    release();
  }
}

/**
 * Requests a URL, returning a promise
 *
 * @param {string} url The URL we want to request
 * @param {RequestInit} [options] The options we want to pass to "fetch"
 * @return {Promise<any>} The response data
 */
export async function baseRequest(
  url: string,
  options?: RequestInit
): Promise<any> {
  /*
    async-mutex используется для потокобезопасности при запросе обновления
    токенов
    https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#preventing-multiple-unauthorized-errors
    https://github.com/DirtyHairy/async-mutex
  */
  await refresh_tokens_mutex.waitForUnlock();
  let fetchResponse = await fetch(url, { ...options, credentials: 'include' });

  if (fetchResponse.status === 204) {
    return fetchResponse;
  }

  const contentType = fetchResponse.headers.get('content-type');

  if (!contentType) {
    throw new ResponseError(fetchResponse);
  }

  if (!fetchResponse.ok && contentType.includes('application/json')) {
    const body = await fetchResponse.clone().json();

    if (fetchResponse.status === 401 || fetchResponse.status === 403) {
      const { detail, error } = body;

      if (
        [
          'Tokens need to be updated',
          'Given token not valid for any token type',
        ].includes(detail) ||
        error === 'token_expired'
      ) {
        // Handle token refresh
        if (!refresh_tokens_mutex.isLocked()) {
          fetchResponse = await refreshTokenAndRetry(url, options);
        } else {
          await refresh_tokens_mutex.waitForUnlock();
          fetchResponse = await fetch(url, {
            ...options,
            credentials: 'include',
          });
        }
      } else if (
        [
          'Not authenticated',
          'Token is blacklisted',
          'Отсутствует access_token в cookie',
          'Нет токена обновления',
          'CSRF Failed: CSRF cookie not set.',
        ].includes(detail) &&
        !logout_mutex.isLocked()
      ) {
        await logout_mutex.acquire().then((release) => {
          /*
            В случае такого response, приходят 3 set-cookie c удалением
            access_token, refresh_token и csrftoken
          */
          relocateToAuthPageWithReload();
          release();
        });
      }
    } else if (contentType.includes('text/html')) {
      // Текстовое содержимое для ответов HTML
      return fetchResponse.text();
    }
  }

  return checkStatus(fetchResponse, url);
}

export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export async function gatewayRequest(
  url: string,
  options?: RequestInit
): Promise<any> {
  const fetchResponse = await baseRequest(url, options);

  return parseJSON(fetchResponse);
}

export async function gatewayUnparsedRequest(
  url: string,
  options?: RequestInit
): Promise<any> {
  return await baseRequest(url, options);
}

/**
 * Типобезопасная функция для получения файлов по пути из IRequest.
 * Автоматически извлекает файлы из вложенных структур по строковому пути.
 *
 * @example
 * getAttachmentFiles(request, 'requester_attachment')
 * getAttachmentFiles(request, 'execution.attachment')
 * getAttachmentFiles(request, 'execution.act')
 */
export const getRequestAttachmentFiles = (
  request: IRequest,
  field: IRequestAttachmentsField
): IAttachmentFile[] => {
  if (!request || !field) {
    return [];
  }

  /** Разбить путь на части */
  const path = (field as string).split('.') as [keyof IRequest, ...string[]];

  /** Навигация по пути */
  let current: any = request;

  for (const key of path) {
    current = current?.[key];

    if (!current) {
      return [];
    }
  }

  /** Возврат массива файлов или пустого массива */
  return current?.files || [];
};
